import { Injectable } from '@angular/core';
import { Http, Headers } from '@angular/http';
import 'rxjs/add/operator/toPromise';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { AppMemoryService } from './app-memory.service';
import { AppStorageType } from '../models/AppStorageType';
import {
  DownloadPresignedUrls,
  PresignedResponse,
  UploadFile,
} from './classes/storage';

@Injectable()
export class StorageService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: Http,
    private authService: AuthService,
    private appMemory: AppMemoryService
  ) {}

  /** Single source of truth for the storage-backend decision. */
  public isS3(): boolean {
    return (
      !!this.appMemory.static &&
      this.appMemory.static.appStorageType === AppStorageType.s3
    );
  }

  // ---- UPLOAD -------------------------------------------------------------

  /**
   * Prepares `formData` for the chosen backend.
   *  - filesystem: appends raw file bytes under `fieldName`.
   *  - s3: appends the `presigned_urls` JSON descriptor.
   * Returns true when a follow-up S3 PUT is required after the record saves.
   */
  public appendFilesToForm(
    formData: FormData,
    fieldName: string,
    files: UploadFile[],
    descriptorExtra?: any,
    indexed?: boolean
  ): boolean {
    if (this.isS3()) {
      // s3 mode sends only an upload *descriptor* (content type / filename) — never
      // the bytes. The backend needs the content type to build the presigned POST
      // policy, and reads it from the same field naming filesystem mode uses: flat
      // `microchip[content_type]` or nested `images[i][content_type]`.
      files.forEach((f, i) => {
        const prefix = indexed ? fieldName + '[' + i + ']' : fieldName;
        formData.append(prefix + '[content_type]', f.file.type || 'application/octet-stream');
        formData.append(prefix + '[filename]', f.file.name);
        if (descriptorExtra) {
          Object.keys(descriptorExtra).forEach((k) =>
            formData.append(prefix + '[' + k + ']', descriptorExtra[k])
          );
        }
      });
      return true;
    }
    // `indexed` preserves the legacy nested collection naming
    // (`images[i][content]`); flat fields (e.g. `microchip`) omit it. This keeps
    // filesystem-mode form-data byte-for-byte identical to before.
    files.forEach((f, i) => {
      const field = indexed ? fieldName + '[' + i + '][content]' : fieldName;
      formData.append(field, f.file, f.file.name);
    });
    return false;
  }

  /**
   * Uploads every file to S3 via its presigned POST policy (multipart POST, not a
   * PUT). Resolves only when ALL succeed; rejects on the first failure so callers
   * can surface an error and offer a retry. (Name kept for its existing call sites.)
   */
  public putToS3(
    presigned: PresignedResponse[],
    files: UploadFile[]
  ): Promise<any[]> {
    // Backend issues presigned POST policies (Aws\S3\PostObjectV4): each upload is
    // a multipart POST with every policy field first and the file appended LAST as
    // `file`. Entries are order-aligned with `files`. No auth header — S3 rejects
    // requests carrying Authorization (the Http interceptor skips non-API URLs, so
    // it neither attaches one nor logs the user out on an S3 error).
    const posts = presigned.map((resp, i) => {
      const upload = files[i];
      if (!upload || !resp || !resp.url) {
        return Promise.resolve(null);
      }
      const body: FormData = new FormData();
      const fields = resp.fields || {};
      Object.keys(fields).forEach((name) => body.append(name, fields[name]));
      body.append('file', upload.file);
      return this.http.post(resp.url, body).toPromise();
    });
    return Promise.all(posts);
  }

  // ---- DOWNLOAD -----------------------------------------------------------

  /**
   * filesystem: returns the path unchanged.
   * s3: the API already serializes assets as signed GET URLs, so an absolute
   * URL is usable as-is and needs no round-trip. Only a raw object key is
   * exchanged via /storage/getPresignedUrl.
   */
  public getDownloadUrl(path: string): Promise<string> {
    if (!this.isS3() || this.isAbsoluteUrl(path)) {
      return Promise.resolve(path);
    }
    return this.fetchPresignedDownloadUrls([{ path: path, url: null }]).then(
      (res) => res[0].url
    );
  }

  /** Batched variant preserving input order. */
  public getDownloadUrls(paths: string[]): Promise<DownloadPresignedUrls[]> {
    const passThrough = !this.isS3() || paths.every((p) => this.isAbsoluteUrl(p));
    if (passThrough) {
      return Promise.resolve(
        paths.map((p) => ({ path: p, url: p }) as DownloadPresignedUrls)
      );
    }
    const assetPaths = paths.map(
      (p) => ({ path: p, url: null }) as DownloadPresignedUrls
    );
    return this.fetchPresignedDownloadUrls(assetPaths);
  }

  private isAbsoluteUrl(path: string): boolean {
    return !!path && /^https?:\/\//i.test(path);
  }

  private fetchPresignedDownloadUrls(
    assetPaths: DownloadPresignedUrls[]
  ): Promise<DownloadPresignedUrls[]> {
    return this.http
      .post(
        this.apiUrl + '/storage/getPresignedUrl',
        { assetPaths: assetPaths },
        { headers: this.authHeaders() }
      )
      .toPromise()
      .then((response) => response.json() as DownloadPresignedUrls[]);
  }

  private authHeaders(): Headers {
    const headers = new Headers({ Accept: 'application/json' });
    if (this.authService.isLoggedIn) {
      headers.append('Authorization', 'Bearer ' + this.authService.getToken());
    }
    return headers;
  }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/emr-webapp/src/app/core/storage.service.ts